import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, KeyRound, Radar, ShieldCheck, Sparkles } from "lucide-react";
import { useState } from "react";
import { PageHeader } from "@/components/domain/misc";
import { Page, QueryState } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/misc";
import { OnboardingChecklist } from "@/features/onboarding/checklist";
import { CredentialsStep, type SetupForm } from "@/features/setup/credentials-step";
import { ControlsStep } from "@/features/setup/controls-step";
import { InstrumentStep } from "@/features/setup/instrument-step";
import { LiveStatusPill, useLatestTrace, useResolvedAgent, VerifyPanel } from "@/features/setup/live-status";
import { PathPicker } from "@/features/setup/path-picker";
import { type Built, BuiltPicker, isBuilt, isPlatformOption, PLATFORM_META, PlatformConnectStep, type PlatformOption, PlatformPicker } from "@/features/setup/platform-step";
import { isFramework, isSetupPath, PATH_META, type Framework, type SetupPath } from "@/features/setup/paths";
import { ingestBaseOf, type SnippetCtx } from "@/features/setup/snippets";
import { Step, Steps } from "@/features/setup/stepper";
import { VerifyStep } from "@/features/setup/verify-step";
import { q } from "@/lib/api";

export const Route = createFileRoute("/_app/setup")({
  validateSearch: (search: Record<string, unknown>): { built?: Built; platform?: PlatformOption; path?: SetupPath; fw?: Framework } => ({
    ...(isBuilt(search.built) ? { built: search.built } : {}),
    ...(isPlatformOption(search.platform) ? { platform: search.platform } : {}),
    ...(isSetupPath(search.path) ? { path: search.path } : {}),
    ...(isFramework(search.fw) ? { fw: search.fw } : {}),
  }),
  component: SetupPage,
});

const NEXT = [
  {
    icon: <Sparkles />,
    title: "Review the risk profile",
    body: "Adaptive profiling infers tools, action classes, authority and who the agent acts for, then turns on the matching detector packs."
  },
  {
    icon: <Radar />,
    title: "Baseline and first signals",
    body: "Detectors run on every trace from the first minute. Behavioral baselines become credible after a few thousand sessions.",
  },
  {
    icon: <ShieldCheck />,
    title: "Coverage that grows",
    body: "Add the agent to a policy. Issues get a covered grace window to fix; platform-reported controls and resolved issues raise its score and limit.",
  },
];

function SetupPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  // Existing deep links (`?path=`) are in-house setups; otherwise start with the platform path.
  const built: Built = search.built ?? (search.path || search.fw ? "inhouse" : "platform");
  const platform: PlatformOption = search.platform ?? "lumen-labs";
  const platformName = PLATFORM_META[platform].label;
  const path: SetupPath = search.path ?? "typescript";
  const fw: Framework = search.fw ?? "claude";

  const settings = useQuery(q.settings());
  const agents = useQuery(q.agents());
  const [form, setForm] = useState<Partial<SetupForm>>({});
  const values: SetupForm = {
    agent: form.agent ?? "my-agent",
    environment: form.environment ?? "prod",
    privacyMode: form.privacyMode ?? settings.data?.privacyMode ?? "redacted",
  };
  const agentName = values.agent.trim() || "my-agent";
  const resolved = useResolvedAgent(agentName);
  const latest = useLatestTrace(resolved.agentId);
  const receiving = Boolean(latest.data?.items.length);

  const ctx: SnippetCtx | undefined = settings.data
    ? { agent: agentName, environment: values.environment, privacyMode: values.privacyMode, ingestBase: ingestBaseOf(settings.data.ingestEndpoint) }
    : undefined;

  const setFramework = (next: Framework) => void navigate({ search: (prev) => ({ ...prev, fw: next }), replace: true, resetScroll: false });

  return (
    <Page>
      <PageHeader
        title="Connect an agent"
        description="From zero to first signal in minutes. Tell us where the agent is built, create a key in your workspace, then connect it — through your agent platform's built-in integration, or with an SDK or OpenTelemetry for in-house agents."
        actions={
          <>
            {resolved.loading ? <Skeleton className="h-6 w-44" /> : <LiveStatusPill agentId={resolved.agentId} />}
            <Button variant="outline" size="sm" asChild>
              <Link to="/settings" search={{ tab: "api-keys" }}>
                <KeyRound /> API keys
              </Link>
            </Button>
          </>
        }
      />

      <OnboardingChecklist className="mb-6" />

      <QueryState
        query={settings}
        loading={
          <div className="flex flex-col gap-4">
            <Skeleton className="h-36 w-full" />
            <Skeleton className="h-72 w-full" />
          </div>
        }
      >
        {(s) =>
          !ctx ? null : built === "platform" ? (
            <Steps label="Setup steps">
              <Step n={1} id="built" title="Where is this agent built?" description="Agents built on a platform connect through its built-in integration with your key. In-house agents use an SDK or OpenTelemetry." done>
                <BuiltPicker value={built} />
              </Step>

              <Step n={2} id="platform" title="Which agent platform?" description="You own the account, the key and the policies; the platform only exports your agent's runs to your workspace." done={Boolean(search.platform)}>
                <PlatformPicker value={platform} settings={s} agents={agents.data ?? []} />
              </Step>

              <Step n={3} id="credentials" title="Create a key and name the agent" description="One key per environment, with the ingest scope.">
                <CredentialsStep settings={s} agents={agents.data ?? []} form={values} onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} ctx={ctx} platform />
              </Step>

              <Step
                n={4}
                id="connect"
                title={platform === "other" ? "Paste your key into the platform's integration" : `Paste your key into ${platformName}`}
                description="No code. The platform's built-in traces.insure integration exports every run and reports the controls it enforces."
                done={receiving}
              >
                <PlatformConnectStep platform={platform} settings={s} form={values} ctx={ctx} />
              </Step>

              <Step n={5} id="verify" title="Verify the connection" description="This page polls for traces from your agent and checks what they contain." done={receiving} last>
                <VerifyPanel agentName={ctx.agent} agentId={resolved.agentId} known={Boolean(resolved.agent)} />
              </Step>
            </Steps>
          ) : (
            <Steps label="Setup steps">
              <Step n={1} id="built" title="Where is this agent built?" description="Agents built on a platform connect through its built-in integration with your key. In-house agents use an SDK or OpenTelemetry." done>
                <BuiltPicker value={built} />
              </Step>

              <Step n={2} id="choose" title="Choose how your agent connects" description="You can combine paths later, e.g. an SDK for your code and OpenTelemetry for existing services." done>
                <PathPicker value={path} />
              </Step>

              <Step n={3} id="credentials" title="Create a key and name the agent" description="One key per environment. The SDKs read these variables, so the code below needs no secrets.">
                <CredentialsStep settings={s} agents={agents.data ?? []} form={values} onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} ctx={ctx} />
              </Step>

              <Step
                n={4}
                id="instrument"
                title={path === "otel" ? "Export your existing OpenTelemetry spans" : "Install and instrument"}
                description={
                  path === "framework"
                    ? "Drop-in adapters record runs, model calls and tools. Pick your framework."
                    : path === "otel"
                      ? "Keep your instrumentation. Point an exporter or Collector at traces.insure."
                      : `Wrap a session, an agent run, model calls and tools. ${PATH_META[path].short} snippets below use your agent name.`
                }
                done={receiving}
              >
                <InstrumentStep path={path} fw={fw} onFramework={setFramework} ctx={ctx} />
              </Step>

              <Step n={5} id="controls" title="Report controls from your agent platform" description="Policies on risky tools are enforced by the platform that runs them. It reports each outcome to traces.insure.">
                <ControlsStep />
              </Step>

              <Step n={6} id="verify" title="Verify the connection" description="This page polls for traces from your agent and checks what they contain." done={receiving} last>
                <VerifyStep ctx={ctx} agentId={resolved.agentId} known={Boolean(resolved.agent)} lang={PATH_META[path].lang} />
              </Step>
            </Steps>
          )
        }
      </QueryState>

      <section aria-labelledby="setup-next" className="mt-10">
        <h2 id="setup-next" className="mb-3 text-xs font-semibold uppercase tracking-wide text-fg-3">
          After your first traces
        </h2>
        <div className="grid gap-3 md:grid-cols-3">
          {NEXT.map((n) => (
            <Card key={n.title} className="p-4">
              <div className="flex items-center gap-2 text-[13px] font-semibold text-fg [&_svg]:size-4 [&_svg]:text-accent">
                {n.icon}
                {n.title}
              </div>
              <p className="mt-1.5 text-xs leading-relaxed text-fg-3">{n.body}</p>
            </Card>
          ))}
        </div>
        {resolved.agent ? (
          <div className="mt-3">
            <Button variant="link" size="sm" asChild>
              <Link to="/agents/$agentId" params={{ agentId: resolved.agent.id }}>
                Open {resolved.agent.name} <ArrowRight />
              </Link>
            </Button>
          </div>
        ) : null}
      </section>
    </Page>
  );
}
